import React, { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react'
import './ticket-view.css'
import MainContainer from "../../components/container/MainContainer"; import Breadcrumb from '../../components/breadcrumb/Breadcrumb'
import BreadcrumbData from '../../utils/constants/BreadcrumbData.constants'
import { useLocation, useParams } from 'react-router-dom'
import attachIcon from '../../assets/attach-icon.svg'
import closeIcon from '../../assets/close-blue.svg'
import downloadIcon from '../../assets/card-icons/download-purple.svg'
import { hideLoader, showLoader } from '../../store/Loader/loader.slice'
import { useDispatch } from 'react-redux'
import { postDetails, postMultipartDetails } from '../../services/http/http.service'
import APIList from '../../utils/constants/APIList'
import decrypt from '../../utils/helpers/decrypt'
import { APIResponseCodes } from '../../utils/constants/keys.constants'
import InfoCard from '../../components/Cards/InfoCard'
import openURL from '../../utils/helpers/openURL'
import encrypt from '../../utils/helpers/encrypt'
import sort from '../../utils/helpers/sort'
import formatDate from '../../utils/constants/formatDate'
import { message } from '../../utils/constants/Messages';

const TicketView: React.FunctionComponent<any> = () => {

    const dispatch = useDispatch()
    const location = useLocation()
    const fileRef = useRef(null)
    const isUIMounted = useRef(false)
    const params = useParams()

    const [ticketMetaData, setTicketMetaData] = useState({
        assignee: '',
        id: 0,
        status: '',
        summary: '',
    } as { summary: string, id: number, assignee: string, status: 'initiated' | 'in progress' | 'closed' | 'escalated' | '' })
    const [followUps, setFollowUps] = useState([] as Array<any>)
    const [formInputs, setFormInputs] = useState({ ticketActionInput: '', file: null } as { ticketActionInput: string, file: File | null })
    const [err, setErr] = useState('')
    const [allowEdit, setAllowEdit] = useState(false)

    /** Get ticket details */
    const getTicketDetails = async () => {
        dispatch(showLoader(message.GETTING_TICKET_DETAILS))
        const response = await postDetails(APIList.GET_REQUEST_BY_ID, { id: parseInt(decodeURIComponent(decrypt(params.id))) }, getTicketDetails)
        if (response.statusCode === APIResponseCodes.SUCCESS) {
            setTicketMetaData({
                assignee: response.data?.ticket?.assigned_to?.username || '',
                id: response.data?.ticket?.id || 0,
                status: response.data?.ticket?.status?.toLowerCase() || 'initiated',
                summary: response.data?.ticket?.summary || ''
            })

            if (response.data.followups?.length !== 0) {
                const sorted: any = sort(response.data.followups, 'created_at', 'timestamp')
                if (sorted[0]?.comment_user_type === 2 || sorted[0]?.comment_user_type === 4) {
                    setAllowEdit(false)
                } else {
                    setAllowEdit(true)
                }
                setFollowUps(sorted || [])
            }
        }
        dispatch(hideLoader())
    }

    useEffect(() => {
        if (isUIMounted.current === true) return
        isUIMounted.current = true
        getTicketDetails()
    }, [])

    /** click on input element programmatically */
    const handleFileClick = async () => {
        if (fileRef.current === null) return
        const currentElement: { click: Function } = fileRef.current
        currentElement.click()
    }

    /** click on input element programmatically */
    const onChooseFile = (ev: ChangeEvent<HTMLInputElement>) => {
        if (ev.target.files === null) {
            ev.target.value = ''
            setErr(message.FILE_REQUIRED)
            return
        } else if (ev.target.files[0].size > 5000000) {
            ev.target.value = ''
            setErr(message.FILE_SIZE)
            return
        } else if (!['pdf', 'doc', 'docx'].includes(ev.target.files[0].name.split('.')[ev.target.files[0].name.split('.').length - 1].toLowerCase())) {
            ev.target.value = ''
            setErr(message.FILE_TYPE)
            return
        } else {
            setFormInputs({ ...formInputs, file: ev.target.files[0] })
        }
    }

    /** Reset file & input */
    const resetFileState = () => {
        setFormInputs({ ...formInputs, file: null })
        if (fileRef.current === null) return
        const currentElement: { value: string } = fileRef.current
        currentElement.value = ''
    }

    /** POST comment  */
    const postComment = async (ev: FormEvent<HTMLFormElement>) => {
        ev.preventDefault()
        dispatch(showLoader(message.SENDING))
        const formData = new FormData()
        formData.append('data', encrypt({ ticket_id: ticketMetaData.id, comments: formInputs.ticketActionInput }))
        if (formInputs.file !== null) {
            formData.append('files', formInputs.file, formInputs.file?.name)
        }

        const response = await postMultipartDetails(APIList.POST_REQUEST_COMMENT, formData)

        if (response.statusCode === APIResponseCodes.SUCCESS) {
            const sorted: any = sort([...followUps, response.data], 'created_at', 'timestamp')
            setFollowUps(sorted)
            setFormInputs({ ticketActionInput: '', file: null })
            setAllowEdit(false)
            if (fileRef.current === null) return
            const currentElement: { value: string } = fileRef.current
            currentElement.value = ''
        }

        dispatch(hideLoader())
    }

    return (
        <MainContainer>
            <Breadcrumb routes={[...BreadcrumbData.request, { id: '21', routeLabel: `${decodeURIComponent(decrypt(params.id))}`, route: location.pathname }]} />

            <div className="mf-active-header-padding">
                <div className='mf-ticket-view card-shadow bg-white p-4 position-relative'>
                    <div className="d-flex align-items-center justify-content-between ticket-header-content">
                        <div className="">
                            <p className="mb-0 font-18 weight-600 text-sec-color">{ticketMetaData.summary}</p>
                            <p className="mb-0 font-13 weight-400 text-capitalize text-primary-color mt-1">Assignee - {ticketMetaData.assignee}</p>
                        </div>
                        {
                            ticketMetaData.status.toLowerCase() === 'initiated' &&
                            <button type="button" className="req-status-btn req-status-btn-success weight-500">
                                Initiated
                            </button>
                        }
                        {
                            ticketMetaData.status.toLowerCase() === 'in progress' &&
                            <button type="button" className="req-status-btn req-status-btn-success weight-500">
                                In Progress
                            </button>
                        }
                        {
                            ticketMetaData.status.toLowerCase() === 'closed' &&
                            <button type="button" className="req-status-btn req-status-btn-closed weight-500">
                                Closed
                            </button>
                        }
                        {
                            ticketMetaData.status.toLowerCase() === 'escalated' &&
                            <button type="button" className="req-status-btn req-status-btn-closed weight-500">
                                Escalated
                            </button>
                        }
                    </div>

                    {
                        ticketMetaData.status !== 'closed' &&
                        <form className='ticket-action-form mt-3 d-flex align-items-start' onSubmit={postComment}>
                            <div className="w-100 position-relative">
                                <textarea
                                    className='mf-ticket-textarea'
                                    name="ticketActionInput"
                                    id="ticketTextarea"
                                    rows={1}
                                    onChange={(ev) => setFormInputs({ ...formInputs, ticketActionInput: ev.target.value })}
                                    value={formInputs.ticketActionInput}
                                    disabled={followUps.length === 0 || !allowEdit}
                                    minLength={10}
                                    maxLength={300}
                                    placeholder='Your query (min. 10 characters)'
                                ></textarea>
                                {
                                    formInputs.file !== null &&
                                    <div className="ticket-file-preview d-flex align-items-center justify-content-between p-2">
                                        <div className="">
                                            <img width={19} height={19} src={attachIcon} alt="attachment" />
                                            <p className="mb-0 font-13 weight-500 d-inline ms-2 break-word">{formInputs.file.name}</p>
                                        </div>
                                        <button onClick={resetFileState} type='button' className="border-0">
                                            <img width={19} height={19} src={closeIcon} alt="attachment" />
                                        </button>
                                    </div>
                                }
                            </div>
                            <button disabled={followUps.length === 0 || !allowEdit} onClick={handleFileClick} type='button' className="ticket-action-btn ticket-attach-btn border-0 mx-3 mt-1">
                                <img width={19} height={19} src={attachIcon} alt="attachment" />
                                <input accept='application/msword, application/pdf' onChange={onChooseFile} ref={fileRef} type="file" className='d-none' />
                            </button>
                            <button disabled={followUps.length === 0 || formInputs.ticketActionInput.length < 10 || !allowEdit} type='submit' className="ticket-action-btn ticket-send-btn border-0 mt-1"></button>
                        </form>
                    }

                    {
                        err.length !== 0 &&
                        <p className="error-text-color font-12 mt-2">{err}</p>
                    }


                    <div className="mt-4" hidden={ticketMetaData.status.length === 0}>
                        {
                            followUps.length === 0 ?
                                <InfoCard message={ticketMetaData.status.toLowerCase() !== 'closed' ? 'Request initiated - Our technical person will check your request shortly...' : 'Request - Closed'} status={ticketMetaData.status.toLowerCase() !== 'closed' ? 'warning' : 'error'} />
                                :
                                followUps.map((ticket: any, index: number) => (
                                    <div key={index} className="ticket-timline-container px-3">
                                        <div data-success={((index === 0 && ticketMetaData.status.toLowerCase() !== 'closed') && (ticket?.comment_user_type === 2 || ticket?.comment_user_type === 4) ? "false" : "true")} className="ticket-timline">
                                            <div className="ticket-timline-user">
                                                <p className="mb-0 font-16 weight-600 text-primary-color text-capitalize">
                                                    {ticket?.comment_user_type === 2 || ticket?.comment_user_type === 4 ? 'You' : ticket?.username || 'Admin'}<small className="ms-2 weight-400 text-capitalize">{formatDate(ticket.created_at, 'fullmonth')}</small></p>
                                            </div>
                                            <p className="mb-0 font-13 weight-400 text-primary-color mt-1 ticket-timline-message">{ticket.comments}</p>
                                            {
                                                ticket.sop_document?.length !== 0 &&
                                                <button onClick={() => openURL(ticket.sop_document, 'download', ticket.sop_document_filename || 'SOP-Document')} type='button' className="d-flex align-items-center justify-content-between ticket-timeline-attachment mt-2">
                                                    <p className="mb-0 weight-500 me-3">{ticket?.sop_document_filename || 'SOP Document'}</p>
                                                    <img width={16} height={16} src={downloadIcon} alt="download" />
                                                </button>
                                            }
                                        </div>
                                    </div>
                                ))
                        }
                        {
                            followUps.length !== 0 &&
                            <div className="ticket-timline-container px-3">
                                <div data-success="true" className="ticket-timline">
                                    <div className="ticket-timline-user">
                                        <p className="mb-0 font-16 weight-600 text-primary-color text-capitalize">You</p>
                                    </div>
                                    <p className="mb-0 font-13 weight-400 text-primary-color mt-1 text-capitalize ticket-timline-message">Request initiated</p>
                                </div>
                            </div>
                        }
                    </div>

                </div>

            </div>

        </MainContainer>
    )
}

export default TicketView